import { useState } from 'react';
import { Body1, Body2 } from '@ui/atoms/typography';
import { Button } from '@ui/atoms/button';
import { DropdownCustom } from '@ui/molecules/dropdown-custom';
import { DropdownItem } from '@pages/dashboard/you/you-page.styles';
import { CalenderIcon } from '@ui/atoms/icons/calender-icon';
import { PlusSVG } from '@ui/atoms/svg';
import { DateRangeInput } from '@ui/molecules/date-range-input';
import dayjs from 'dayjs';

interface TimeRangeDropdownProps {
  selectedRange: string;
  onRangeSelect: (range: string) => void;
  showPlusIcon?: boolean;
}

export const TimeRangeDropdown = ({
  selectedRange,
  onRangeSelect,
  showPlusIcon = true,
}: TimeRangeDropdownProps) => {
  const [showCustomDateFilter, setShowCustomDateFilter] = useState(false);
  const [dates, setDates] = useState({
    starts: dayjs().subtract(30, 'days').format('MM/DD/YYYY'),
    ends: dayjs().format('MM/DD/YYYY'),
  });

  const currentYear = dayjs().year();
  const previousYear = currentYear - 1;

  const timeRangeOptions = [
    'All time',
    'Last 12 months',
    String(currentYear),
    String(previousYear),
    'Custom Range',
  ];

  const handleOptionSelect = (option: string, handleClose: () => void) => {
    if (option === 'Custom Range') {
      setShowCustomDateFilter(true);
    } else {
      setShowCustomDateFilter(false);
      onRangeSelect(option);
      handleClose();
    }
  };

  const handleApplyCustomDate = (handleClose: () => void) => {
    if (new Date(dates.ends) >= new Date(dates.starts)) {
      const customRangeString = showPlusIcon
        ? `Custom: ${dates.starts} - ${dates.ends}`
        : `${dates.starts} - ${dates.ends}`;
      onRangeSelect(customRangeString);
      setShowCustomDateFilter(false);
      handleClose();
    }
  };

  const handleCancel = () => {
    setShowCustomDateFilter(false);
  };

  const isInvalidDateRange = () => {
    return new Date(dates.ends) < new Date(dates.starts);
  };

  return (
    <DropdownCustom
      collapseOnClick={!showCustomDateFilter}
      menu={(handleClose: () => void) => (
        <div className="min-h-[280px]">
          {showCustomDateFilter ? (
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <Body1 weight="semibold">Select date range</Body1>
                <div className="flex gap-2">
                  <Button
                    kind="secondary"
                    style={{ padding: '8px 16px' }}
                    onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button
                    kind="primary"
                    style={{ padding: '8px 16px' }}
                    onClick={() => handleApplyCustomDate(handleClose)}
                    disabled={isInvalidDateRange()}>
                    Apply
                  </Button>
                </div>
              </div>
              <DateRangeInput
                name="date"
                value={{ starts: dates.starts, ends: dates.ends }}
                block={true}
                helper={
                  isInvalidDateRange()
                    ? 'Start date must be before end date'
                    : ''
                }
                state={isInvalidDateRange() ? 'error' : 'default'}
                admin={true}
                placeholder="Select Date"
                setValue={(e: { starts: string; ends: string }) => {
                  setDates({
                    starts: dayjs(e.starts).format('MM/DD/YYYY'),
                    ends: dayjs(e.ends).format('MM/DD/YYYY'),
                  });
                }}
              />
            </div>
          ) : (
            <div className="p-2">
              {timeRangeOptions.map((option) => (
                <DropdownItem
                  key={option}

                  onClick={() => handleOptionSelect(option, handleClose)}
                  background={selectedRange === option ? '#F6F6F8' : 'none'}>
                  <div className="flex items-center gap-2">
                    {option === 'Custom Range' && showPlusIcon && (
                      <PlusSVG style={{ height: 18, width: 18 }} />
                    )}
                    <span>
                      {option === 'Custom Range' ? 'Custom Range' : option}
                    </span>
                  </div>
                </DropdownItem>
              ))}
            </div>
          )}
        </div>
      )}
      customComponent={(handleOpen: (event: any) => void) => (
        <Button
          kind="secondary"
          onClick={(e: any) => handleOpen(e)}
          style={{ padding: '6px 16px' }}>
          <div className="flex items-center gap-2">
            <Body2 weight="semibold" kind="textDark">
              {selectedRange}
            </Body2>
            <CalenderIcon style={{ marginLeft: '4px' }} />
          </div>
        </Button>
      )}
      customDropdownWrapperStyles={{
        top: '130%',
        width: '320px',
        padding: '0',
        right: 0,
        minHeight: '280px',
      }}
    />
  );
};
